import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  Grid, FormControl, FormControlLabel, InputLabel,
  MenuItem, Select, TextField,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import * as XLSX from "xlsx";
import moment from "moment";

import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import HistoryIcon from "@material-ui/icons/History";
import ForwardIcon from "@material-ui/icons/Forward";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import AssessmentIcon from "@material-ui/icons/Assessment";

import { blue, green } from "@material-ui/core/colors";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";
import { UsersFilter } from "../../components/UsersFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ToggleSwitch from "../../components/ToggleSwitch";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
  },
  filterPaper: {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
  },
  tablePaper: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
    maxHeight: "60vh",
    ...theme.scrollbarStyles,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  ticketCount: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    background: theme.palette.action.hover,
    borderRadius: 20,
    padding: "2px 10px",
    fontWeight: 600,
  },
  statusChip: {
    fontSize: "0.7rem",
    height: 20,
    borderRadius: 4,
    fontWeight: 600,
  },
  // Mobile cards
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.8),
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  cardId: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    fontWeight: 700,
  },
  cardContact: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardMeta: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  cardMsg: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 4,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(0.5),
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
}));

const statusColors = {
  open:    { bg: "rgba(91,45,142,0.12)",  color: "#5B2D8E",  label: "Abierto"  },
  closed:  { bg: "rgba(34,197,94,0.12)",  color: "#166534",  label: "Cerrado"  },
  pending: { bg: "rgba(247,144,9,0.12)",  color: "#92400e",  label: "Pendiente"},
};

const IconChannel = (channel) => {
  if (channel === "facebook") return <FacebookIcon style={{ color: "#3b5998", fontSize: 16 }} />;
  if (channel === "instagram") return <InstagramIcon style={{ color: "#e1306c", fontSize: 16 }} />;
  return <WhatsAppIcon style={{ color: "#25d366", fontSize: 16 }} />;
};

const Reports = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { getReport } = useDashboard();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", { params: { searchParam } });
        setOptions(data.contacts);
      } catch (err) { toastError(err); }
      finally { setLoading(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam]);

  const buildParams = (page) => ({
    searchParam,
    contactId: selectedContactId,
    whatsappId: JSON.stringify(selectedWhatsapp),
    users: JSON.stringify(userIds),
    queueIds: JSON.stringify(queueIds),
    status: JSON.stringify(selectedStatus),
    dateFrom, dateTo,
    page,
    pageSize,
    onlyRated: onlyRated ? "true" : "false",
  });

  const handleFilter = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getReport(buildParams(page));
      setTotalTickets(data.totalTickets.total);
      setHasMore(data.tickets.length === pageSize);
      setTickets(data.tickets);
      setPageNumber(page);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const [hasMore, setHasMore] = useState(false);

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const data = await getReport({ ...buildParams(1), pageSize: 9999999 });
      const rows = data.tickets.map((t) => ({
        ID: t.id,
        Conexión: t.whatsappName,
        Contacto: t.contactName,
        Usuario: t.userName,
        Cola: t.queueName,
        Status: t.status,
        "Último mensaje": t.lastMessage,
        "Fecha apertura": t.createdAt,
        "Fecha cierre": t.closedAt,
        "Tiempo atención": t.supportTime,
        NPS: t.NPS || "",
        "Valor venta": t.valorVenda ?? "-",
        "Motivo no venta": t.motivoNaoVenda || "-",
        "Cerrado con venta": t.finalizadoComVenda === true ? "Sí" : t.finalizadoComVenda === false ? "No" : "-",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [8, 15, 25, 20, 15, 12, 30, 20, 20, 18, 8, 15, 20, 18].map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, "Reporte");
      XLSX.writeFile(wb, `reporte-atenciones-${dateFrom}-${dateTo}.xlsx`);
      toast.success(`${rows.length} registros exportados`);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const filter = createFilterOptions({ trim: true });
  const renderOption = (option) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {option.channel && IconChannel(option.channel)}
      <span style={{ fontSize: 14 }}>{option.name}{option.number ? ` - ${option.number}` : ""}</span>
    </div>
  );
  const renderOptionLabel = (option) => option.number ? `${option.name} - ${option.number}` : option.name;
  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3)
      filtered.push({ name: params.inputValue });
    return filtered;
  };

  const getStatusStyle = (status) => statusColors[status] || { bg: "rgba(0,0,0,0.06)", color: "#555", label: status };

  const renderMobileCard = (ticket) => {
    const st = getStatusStyle(ticket.status);
    return (
      <div key={ticket.id} className={classes.card}>
        <div className={classes.cardTop}>
          <span className={classes.cardId}>#{ticket.id}</span>
          <span className={classes.cardContact}>{ticket.contactName}</span>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: st.bg, color: st.color, flexShrink: 0 }}>
            {st.label}
          </span>
        </div>
        <div className={classes.cardMeta}>
          {ticket.whatsappName && <span>📱 {ticket.whatsappName}</span>}
          {ticket.userName && <span>👤 {ticket.userName}</span>}
          {ticket.queueName && <span>🏷 {ticket.queueName}</span>}
          {ticket.supportTime && <span>⏱ {ticket.supportTime}</span>}
          {ticket.NPS && <span>⭐ {ticket.NPS}</span>}
        </div>
        {ticket.lastMessage && (
          <div className={classes.cardMsg}>{ticket.lastMessage}</div>
        )}
        <div className={classes.cardMeta}>
          {ticket.createdAt && <span>Apertura: {ticket.createdAt}</span>}
          {ticket.closedAt && <span>Cierre: {ticket.closedAt}</span>}
        </div>
        <div className={classes.cardActions}>
          <Tooltip title={i18n.t("reports.table.logTicket")}>
            <IconButton size="small" onClick={() => { setOpenTicketMessageDialog(true); setTicketOpen(ticket); }}>
              <HistoryIcon fontSize="small" style={{ color: blue[700] }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("reports.table.accessTicket")}>
            <IconButton size="small" onClick={() => history.push(`/tickets/${ticket.uuid}`)}>
              <ForwardIcon fontSize="small" style={{ color: green[700] }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      {openTicketMessageDialog && (
        <ShowTicketLogModal
          isOpen={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticketOpen.id}
        />
      )}

      <MainHeader>
        <div className={classes.headerRow}>
          <div className={classes.headerLeft}>
            <Title>{i18n.t("reports.title")}</Title>
            {totalTickets > 0 && (
              <span className={classes.ticketCount}>{totalTickets} registros</span>
            )}
          </div>
          <div className={classes.headerRight}>
            <Tooltip title="Exportar Excel">
              <IconButton size="small" onClick={exportToExcel} disabled={loading}>
                <SaveAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              color="primary"
              onClick={() => setShowFilter((p) => !p)}
              startIcon={showFilter ? <ClearIcon /> : <FilterListIcon />}
            >
              {showFilter ? "Ocultar" : "Filtros"}
            </Button>
          </div>
        </div>
      </MainHeader>

      {/* Filtros */}
      {showFilter && (
        <div className={classes.filterPaper}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth size="small"
                options={options}
                loading={loading}
                clearOnBlur autoHighlight freeSolo clearOnEscape
                getOptionLabel={renderOptionLabel}
                renderOption={renderOption}
                filterOptions={createAddContactOption}
                onChange={(e, v) => { if (v) setSelectedContactId(v.id); else setSelectedContactId(null); }}
                renderInput={(params) => (
                  <TextField
                    {...params} label={i18n.t("newTicketModal.fieldLabel")}
                    variant="outlined" size="small"
                    onChange={(e) => setSearchParam(e.target.value)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress color="inherit" size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <WhatsappsFilter onFiltered={(s) => setSelectedWhatsapp(s.map((t) => t.id))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatusFilter onFiltered={(s) => setSelectedStatus(s.map((t) => t.status))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <UsersFilter onFiltered={(s) => setUserIds(s.map((t) => t.id))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <QueueSelectCustom selectedQueueIds={queueIds} onChange={(v) => setQueueIds(v)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={i18n.t("reports.startDate")} type="date"
                value={dateFrom} variant="outlined" fullWidth size="small"
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label={i18n.t("reports.endDate")} type="date"
                value={dateTo} variant="outlined" fullWidth size="small"
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={5} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <ToggleSwitch
                checked={!!onlyRated}
                onChange={() => setOnlyRated((p) => !p)}
                label={i18n.t("reports.buttons.onlyRated")}
              />
              <Button variant="contained" color="primary" size="small"
                onClick={() => handleFilter(1)} disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {i18n.t("reports.buttons.filter")}
              </Button>
            </Grid>
          </Grid>
        </div>
      )}

      {/* Contenido */}
      {isMobile ? (
        <Paper className={classes.mainPaper} variant="outlined" style={{ borderRadius: 12 }}>
          {tickets.length === 0 && !loading ? (
            <div className={classes.emptyState}>
              <AssessmentIcon style={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body2">Aplica filtros y presiona Filtrar</Typography>
            </div>
          ) : (
            <div className={classes.mobileList}>
              {tickets.map(renderMobileCard)}
              {loading && <Typography variant="caption" align="center" color="textSecondary">Cargando...</Typography>}
            </div>
          )}
        </Paper>
      ) : (
        <Paper className={classes.tablePaper} variant="outlined">
          <div className={classes.tableContainer}>
            <Table size="small" id="grid-attendants" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">ID</TableCell>
                  <TableCell>{i18n.t("reports.table.whatsapp")}</TableCell>
                  <TableCell>{i18n.t("reports.table.contact")}</TableCell>
                  <TableCell>{i18n.t("reports.table.user")}</TableCell>
                  <TableCell>{i18n.t("reports.table.queue")}</TableCell>
                  <TableCell align="center">{i18n.t("reports.table.status")}</TableCell>
                  <TableCell>{i18n.t("reports.table.lastMessage")}</TableCell>
                  <TableCell align="center">{i18n.t("reports.table.dateOpen")}</TableCell>
                  <TableCell align="center">{i18n.t("reports.table.dateClose")}</TableCell>
                  <TableCell align="center">{i18n.t("reports.table.supportTime")}</TableCell>
                  <TableCell align="center">{i18n.t("reports.table.NPS")}</TableCell>
                  <TableCell align="center">{i18n.t("reports.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" style={{ padding: 40, color: theme.palette.text.secondary }}>
                      Aplica filtros y presiona Filtrar
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {tickets.map((ticket) => {
                      const st = getStatusStyle(ticket.status);
                      return (
                        <TableRow key={ticket.id} hover>
                          <TableCell align="center">{ticket.id}</TableCell>
                          <TableCell>{ticket.whatsappName}</TableCell>
                          <TableCell>{ticket.contactName}</TableCell>
                          <TableCell>{ticket.userName}</TableCell>
                          <TableCell>{ticket.queueName}</TableCell>
                          <TableCell align="center">
                            <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: st.bg, color: st.color }}>
                              {st.label}
                            </span>
                          </TableCell>
                          <TableCell style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {ticket.lastMessage}
                          </TableCell>
                          <TableCell align="center">{ticket.createdAt}</TableCell>
                          <TableCell align="center">{ticket.closedAt}</TableCell>
                          <TableCell align="center">{ticket.supportTime}</TableCell>
                          <TableCell align="center">{ticket.NPS || "—"}</TableCell>
                          <TableCell align="center">
                            <Tooltip title={i18n.t("reports.table.logTicket")}>
                              <IconButton size="small" onClick={() => { setOpenTicketMessageDialog(true); setTicketOpen(ticket); }}>
                                <HistoryIcon fontSize="small" style={{ color: blue[700] }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("reports.table.accessTicket")}>
                              <IconButton size="small" onClick={() => history.push(`/tickets/${ticket.uuid}`)}>
                                <ForwardIcon fontSize="small" style={{ color: green[700] }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {loading && <TableRowSkeleton columns={12} />}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </Paper>
      )}

      {/* Paginación */}
      <div className={classes.paginationRow}>
        <Pagination
          count={Math.ceil(totalTickets / pageSize)}
          page={pageNumber}
          onChange={(e, v) => handleFilter(v)}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>{i18n.t("tickets.search.ticketsPerPage")}</InputLabel>
          <Select
            value={pageSize}
            onChange={(e) => { setPageSize(e.target.value); setPageNumber(1); }}
            label={i18n.t("tickets.search.ticketsPerPage")}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </div>
    </MainContainer>
  );
};

export default Reports;
